import * as JSPDF from 'jspdf-yworks';
import svg2pdf from 'svg2pdf.js';
// import svg2pdf from 'svg2pdf.js/dist/svg2pdf.js';
import _ from 'lodash';

import loadFonts, { listFonts } from './Base64FontLoader';

export default class JSPDFRenderer {
    constructor({
        unit = 'pt',
        format = 'letter',
        baseFont,
        debug
    }) {
        this.pdf = new JSPDF({ unit, format, putOnlyUsedFonts: true });
        // this.pdf = new JSPDF({ unit, format });
        this.baseFont = baseFont;
        loadFonts(this.pdf);
        // console.info('Custom Fonts Available:', listFonts());
        // console.info('Fonts Loaded:', this.pdf.getFontList());
        const customFont = listFonts()[this.baseFont.family];
        const fontMsg = 'Community Profiles PDF Reports Using Font Family';
        if (customFont) {
            console.info(`${fontMsg}: ${this.baseFont.family} Styles:${_.map(customFont, 'ttf')}`);
        } else {
            const chosenFont = this.pdf.getFontList()[this.baseFont.family];
            if (chosenFont) {
                console.info(`${fontMsg}: ${this.baseFont.family} Styles:${chosenFont}`);
            } else {
                console.warn(`Community Profiles PDF Reports font not loaded:${this.baseFont.family}`);
                console.info(`${fontMsg}: ${this.pdf.getFont().fontName}`);
            }
        }
        // internal calc based on the page format,
        this.width = this.pdf.internal.pageSize.getWidth();
        this.height = this.pdf.internal.pageSize.getHeight();
        this.center = this.width / 2;
        this.debug = debug;
    }

    get calcTextHeight() {
        return ({ text, w = this.width, font = this.baseFont } = {}) => {
            // const fontToUse = _.assign({}, this.baseFont, font);
            const fontToUse = this.font(font);
            const paragraphs = _.flatten([text]);
            let textHeight = 0;
            _.forEach(paragraphs, (p) => {
                textHeight += this.calcParaHeight({ text: p, w });
            });
            return textHeight + ((paragraphs.length - 1) * fontToUse.size);
        };
    }

    calcParaHeight({ text, w = this.width }) {
        const lines = this.pdf.splitTextToSize(text, w);
        return (lines.length) * this.pdf.getFontSize() * this.pdf.getLineHeightFactor();
    }

    log(...args) {
        if (this.debug) {
            console.debug(...args);
        }
    }

    get pageSize() {
        return this.pdf.internal.pageSize;
    }

    get pageWidth() {
        return this.pageSize.getWidth();
    }

    get pageHeight() {
        return this.pageSize.getHeight();
    }

    addPage() {
        this.pdf.addPage();
    }

    page(page, newPage, referencesPage) {
        if (newPage || page.num) {
            this.addPage();
        }
        this.referencesPage = referencesPage + 1;
        this.content(page);
        return page;
    }

    save(fileName, props) {
        if (this.error) {
            console.warn('Errors creating PDF.  Last error:', this.error);
            // NOTE: When debugging in browserstack, don't actually save
            // when there is an error:
            // return;
        }
        this.pdf.setDocumentProperties(props);
        this.pdf.save(fileName);
    }

    // Renders a section of a page
    // TODO: Could specify an optional layout that acts as viewport/bounds,
    // TODO: beginFormObject(x, y, width, height, matrix) → {jsPDF}
    content(content) {
        const { background, text, img, svgData, border, href } = content;
        if (background) {
            this.rect(content, { ...background, fill: background.colour });
        }
        if (img) {
            this.image(content);
        }
        if (href) {
            this.href(content);
        } else if (text) {
            this.text(content);
        }

        if (svgData) {
            this.svg(content);
        }
        _.forEach(content, (node) => {
            if (node && node.contentType) {
                this.content(node);
            }
        });
        if (border) {
            this.rect(content, border);
        }
    }

    href({ text, font = this.baseFont, x, y, w, align = 'left', baseline = 'top', href }) {
        this.font(font);
        let alignedX = x;
        let alignedY = y;
        if (align === 'right') {
            alignedX += w - this.pdf.getTextWidth(text);
        } else if (align === 'center') {
            alignedX += w / 2 - this.pdf.getTextWidth(text) / 2;
        }
        // baseline not supported in jsPDF links.
        // See https://github.com/MrRio/jsPDF/issues/2474
        // For now, we support only default (alphabetic) and this hack for top:
        // TODO: if above bug fixed, should be able to just add the baseline to the
        // options parameter in textWithLink below.
        if (baseline === 'top') {
            alignedY += font.size * (1 / this.pdf.getLineHeightFactor());
        }
        this.pdf.textWithLink(text, alignedX, alignedY, { url: href });
    }

    markdown({ text, font, x, y, w, align = 'left', baseline = 'top', markdown }) {
        /* eslint-disable no-useless-escape */
        const regexes = {
            bolditalic: /[\*\_]{3}([^\*\_]+)[\*\_]{3}/g,
            bold: /[\*\_]{2}([^\*\_]+)[\*\_]{2}/g,
            italic: /[\*\_]{1}([^\*\_]+)[\*\_]{1}/g,
            link: /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
        };
        // placeholder delimiter when replacing text
        const delim = '****';

        /* eslint-enable no-useless-escape */
        let toPrint = text.replace(regexes.bold, '$1');
        toPrint = toPrint.replace(regexes.italic, '$1');
        toPrint = toPrint.replace(regexes.bolditalic, '$1');
        const lines = this.pdf.splitTextToSize(text, w);

        let lineY = y;
        for (let i = 0; i < lines.length; i++) {
            let line = lines[i];
            const matches = [];
            let unmarkedText = line;
            // eslint-disable-next-line
            _.forEach(regexes, (regex, key) => {
                const regExp = RegExp(regex);
                const fontMatches = line.matchAll(regExp);
                let fontColour = null;
                let fontType = key === 'link' ? 'bolditalic' : key;
                if (markdown[key]) {
                    fontColour = markdown[key].colour;
                    // eslint-disable-next-line
                    fontType = markdown[key].type;
                }
                // eslint-disable-next-line
                for (const match of fontMatches) {
                    const printToken = (key === 'link') ? match[0] : match[1];
                    // eslint-disable-next-line
                    matches.push({ fontType, fontColour, toPrint: printToken, index: match.index });
                    unmarkedText = unmarkedText.replace(match[0], delim);
                    line = line.replace(match[0], printToken);
                }
            });
            const unmarkedTexts = unmarkedText.split(delim);
            let nextStartIndex = 0;
            for (let j = 0; j < unmarkedTexts.length; j++) {
                const foundIndex = line.indexOf(unmarkedTexts[j], nextStartIndex);
                matches.push({ fontType: 'normal', toPrint: unmarkedTexts[j], index: foundIndex });
                nextStartIndex += foundIndex;
            }
            let tokenX = x;
            // eslint-disable-next-line
            _.forEach(_.sortBy(matches, 'index'), (token) => {
                if (token.fontType) {
                    this.pdf.setFontType(token.fontType);
                }
                if (token.fontColour) {
                    this.pdf.setTextColor(token.fontColour);
                }
                this.pdf.text(token.toPrint, tokenX, lineY, { align, baseline });
                tokenX += this.pdf.getStringUnitWidth(token.toPrint) * font.size;
                // Restore font
                this.font(font);
            });
            lineY += font.size * font.lineHeight;
        }
        return toPrint;
    }

    text({ text, font = this.baseFont, x, y, w, h, align = 'left', baseline = 'top', superscript, markdown }) {
        const fontToUse = this.font(font);
        let alignedX = x;
        if (align === 'right') {
            alignedX += w;
        } else if (align === 'center') {
            alignedX += w / 2;
        }
        let alignedY = y;// + (fontToUse.size * fontToUse.lineHeight);
        const paragraphs = _.flatten([text]);
        let lines = [];
        _.forEach(paragraphs, (p) => {
            let toPrint = p;
            // Some unicode chars not supported by some fonts:
            toPrint = toPrint.replace('‑', '-');
            this.log('text', toPrint, alignedX, alignedY, w, h, align, baseline, fontToUse);
            // const paraHeight = this.calcTextHeight({ text: toPrint, w, font });
            // this.pdf.text(toPrint, alignedX, alignedY, { align, baseline, maxWidth: w });
            if (markdown) {
                toPrint = this.markdown({ text: toPrint, font: fontToUse, x: alignedX, y: alignedY, align, baseline, w, markdown });
            } else {
                this.pdf.text(toPrint, alignedX, alignedY, { align, baseline, maxWidth: w });
            }
            lines = this.pdf.splitTextToSize(toPrint, w);
            alignedY += this.calcParaHeight({ text: toPrint, w }) + fontToUse.size;
        });
        // Use alignedY - lineHeight / 2
        // Use alignedX + lineWidth of
        // Print superscript if exists
        // const lines = this.pdf.splitTextToSize(p, w);
        if (superscript) {
            const superY = alignedY - (fontToUse.size * 1.8);
            const lastLine = _.last(lines);
            const textWidth = this.pdf.getTextWidth(lastLine);
            const superX = alignedX + textWidth - 1;
            this.pdf.setFontSize(fontToUse.size * 0.7);
            this.pdf.setFontType('normal');
            // this.pdf.text(superscript, superX, superY, { baseline: 'bottom' });
            this.pdf.textWithLink(` ${superscript} `, superX, superY, { baseline: 'bottom', pageNumber: this.referencesPage });
        }
    }

    font(font) {
        const fontToUse = _.assign({}, this.baseFont, font);

        this.pdf
            .setFont(fontToUse.family)
            .setFontSize(fontToUse.size)
            .setFontType(fontToUse.type)
            .setTextColor(fontToUse.colour)
            .setLineHeightFactor(fontToUse.lineHeight);
        return fontToUse;
    }

    rect({ h, w, x = 0, y = 0 }, { colour, width, fill }) {
        let style = 'S';
        if (fill) {
            style = 'FD';
            this.pdf.setFillColor(fill);
        }
        if (colour) {
            this.pdf.setDrawColor(colour);
        }
        if (colour && fill) {
            style = 'FD';
        }
        if (width) {
            this.pdf.setLineWidth(width);
        }
        this.log('rect', fill, x, y, w, h, style);
        if (w > 1) {
            this.pdf.rect(x, y, w, h, style);
        } else {
            this.pdf.line(x, y, x, y + h, style);
        }

        this.pdf.setLineWidth(1);
    }

    image({ img, x, y, w, h, type = 'JPEG' }) {
        this.pdf.addImage(img, type, x, y, w, h);
    }

    svg({ svgData, x, y, w, h, font }) {
        this.log('svg', svgData, x, y, w, h);
        // Font does not actually work here, but need to set it to avoid warnings for
        // invalid font variants from previously set fonts in the svg2pdf
        this.font(font);
        try {
            this.pdf.advancedAPI(doc => {
                svg2pdf(svgData, doc, {
                    xOffset: x,
                    yOffset: y,
                    scale: 1
                });
            });
        } catch (e) {
            console.error(`Error Rendering Svg ${svgData}`, e);
            this.pdf.setTransform(1, 0, 0, 1, 0, 0);
            this.text({ text: e.message, x, y, w, h });
            this.error = e;
        }
    }
}
